// The staff session: who is signed in (GET /user), the sign-in / sign-out actions, and the route guard.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { hasSession } from '../services/tokenStore';
import type { Me } from '../types/api';

export type SessionPhase = 'loading' | 'anonymous' | 'not-staff' | 'staff';

export interface Session {
  phase: SessionPhase;
  me: Me | null;
  /** Re-read GET /user after tokens were stored (login, callback). */
  refresh: () => Promise<SessionPhase>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<SessionPhase>(hasSession() ? 'loading' : 'anonymous');
  const [me, setMe] = useState<Me | null>(null);

  const refresh = useCallback(async (): Promise<SessionPhase> => {
    if (!hasSession()) {
      setMe(null);
      setPhase('anonymous');
      return 'anonymous';
    }
    try {
      const user = await api.me();
      setMe(user);
      const next: SessionPhase = user.is_staff ? 'staff' : 'not-staff';
      setPhase(next);
      return next;
    } catch (err) {
      // A 401 is a dead session; anything else (network, 5xx) keeps the page usable and retries on navigation.
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
        setPhase('anonymous');
        return 'anonymous';
      }
      setPhase(hasSession() ? 'staff' : 'anonymous');
      return 'staff';
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setMe(null);
    setPhase('anonymous');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A 401 on any later call (the session expired or was revoked, the refresh refused): admin routes go to /login.
  useEffect(
    () =>
      api.onUnauthorized(() => {
        setMe(null);
        setPhase('anonymous');
      }),
    [],
  );

  const value = useMemo<Session>(() => ({ phase, me, refresh, signOut }), [phase, me, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** Wraps every admin route: anonymous → /login (with the path to come back to); not staff → the refusal. */
export function RequireStaff({ children }: { children: ReactNode }) {
  const { phase, signOut } = useSession();
  const location = useLocation();
  const { t } = useTranslation();
  if (phase === 'loading') return <p className="p-8 text-center text-sm text-faint">{t('common.loading')}</p>;
  if (phase === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (phase === 'not-staff') {
    return (
      <div className="mx-auto mt-24 max-w-md rounded-md border border-line-card bg-white p-6 text-center">
        <h1 className="text-[16px] font-medium text-ink">{t('login.staffOnlyTitle')}</h1>
        <p className="mt-2 text-[13px] text-muted">{t('login.staffOnlyMessage')}</p>
        <button type="button" onClick={() => void signOut()} className="mt-4 rounded-[5px] bg-accent px-4 py-2 text-[12.5px] font-medium text-white hover:bg-accent-hover">
          {t('header.signOut')}
        </button>
      </div>
    );
  }
  return <>{children}</>;
}
